/**
 * Moodboard - tavola di lavoro con immagini manipolabili (Alpine.js)
 *
 * Registrato come componente Alpine "moodboard" (v. app.js) e usato in
 * livewire/moodboard-board.blade.php con x-data="moodboard(prodotti, materiali, loggato, progetto, itemSalvati)",
 * dove progetto = { title, notes } del progetto aperto (vuoti per una tavola nuova) e
 * itemSalvati = item già sulla tavola, con posizione e dimensioni in % del canvas.
 * "Salva" passa la tavola a App\Livewire\MoodboardBoard::save() ($wire); "Download" (App\Livewire\MoodboardDownload) usa save() prima di scaricare il pdf.
 * Utente loggato: uscendo dalla pagina con modifiche non salvate, un modale chiede se salvarle (v. interceptLeave).
 * Guest: "Salva" è disabilitato, la tavola si salva da sola dopo ogni modifica e prima di cambiare pagina (v. autosave).
 */
export default function moodboard(allProducts = [], allMaterials = [], isLogged = false, project = { title: '', notes: '' }, savedItems = []) {
    return {
        // ============================================
        // STATO PRINCIPALE
        // ============================================

        items: [],              // elementi attualmente presenti sulla tavola di lavoro
        selectedItemId: null,   // id dell'elemento correntemente selezionato (null = nessuno)
        activeTab: 'products',  // tab attivo nella sidebar: 'products' oppure 'materials'
        paletteOpen: false,     // palette prodotti/materiali aperta (solo sotto lg, dove è un pannello a scomparsa)

        title: project.title,   // titolo della moodboard
        boardNotes: project.notes, // note generali della moodboard
        isLogged,               // utente loggato: abilita "Download"
        isSaving: false,        // salvataggio in corso
        isSavingNotes: false,   // salvataggio della nota di un elemento in corso
        titleMissing: false,    // "Salva" premuto senza titolo: evidenzia l'input
        _savedItems: savedItems, // item salvati, da posizionare appena il canvas ha una larghezza (v. placeSavedItems)

        // Modifiche non salvate (v. snapshot)
        _savedSnapshot: null,   // stato della tavola all'ultimo salvataggio (o al caricamento)
        showLeaveModal: false,  // modale "modifiche non salvate" aperto
        _leaveUrl: null,        // url della pagina verso cui si sta uscendo
        _leaveWithBarba: false, // l'uscita passa da barba (link interno) o è un caricamento classico
        isLeaving: false,       // uscita confermata: non si intercetta più
        _autosaveTimer: null,   // guest: salvataggio automatico in attesa (v. scheduleAutosave)

        // Stato del modale note
        showNotesModal: false,  // true = modale note aperto
        editingNotes: null,     // id dell'item di cui si stanno modificando le note
        notesText: '',          // testo temporaneo mostrato/modificato nella textarea

        // ============================================
        // DATI SORGENTE (passati dalla blade)
        // ============================================

        // Prodotti/materiali salvati dall'utente, mostrati in palette
        products: allProducts,
        materials: allMaterials,

        // ============================================
        // STATO INTERAZIONE (drag / resize)
        // ============================================

        draggedItem: null,      // id dell'elemento che si sta trascinando (null = nessuno)
        dragStarted: false,     // true solo se il mouse si è mosso abbastanza da considerare "iniziato" il drag
        dragOffset: { x: 0, y: 0 }, // distanza tra il punto cliccato e l'angolo top-left dell'elemento
        dragElementSize: { width: 0, height: 0 }, // dimensioni REALI renderizzate (immagine+titolo+note), usate per il clamp entro i bordi del canvas

        resizingItem: null,     // id dell'elemento in fase di ridimensionamento
        resizeDirection: null,  // direzione della maniglia usata: tl, t, tr, l, r, bl, b, br
        resizeStart: { x: 0, y: 0, width: 0, height: 0, itemX: 0, itemY: 0 }, // valori iniziali al mousedown, usati per calcolare il delta

        canvasWidth: 0,         // ultima larghezza nota del canvas, per riscalare gli elementi quando cambia

        // ============================================
        // INIZIALIZZAZIONE / DISTRUZIONE
        // ============================================

        // chiamato automaticamente da Alpine
        init() {
            // riferimento al componente Livewire: il modale note è teletrasportato nel body
            this._wire = this.$wire;

            // Il drag/resize deve continuare a funzionare anche se il mouse esce dall'elemento,
            // quindi ascoltiamo mousemove/mouseup su tutto il documento
            // (eventi pointer: valgono sia per mouse che per touch)
            this._onMouseMove = (e) => this.handleMouseMove(e);
            this._onMouseUp = () => this.handleMouseUp();
            document.addEventListener('pointermove', this._onMouseMove);
            document.addEventListener('pointerup', this._onMouseUp);
            document.addEventListener('pointercancel', this._onMouseUp);

            // La tavola ha proporzioni fisse (A4 orizzontale) ma larghezza variabile:
            // quando cambia dimensione riscaliamo gli elementi, così la composizione
            // resta identica in proporzione (v. scaleItems)
            this.canvasWidth = this.$refs.canvas.clientWidth;
            this.placeSavedItems();
            this._resizeObserver = new ResizeObserver(() => this.scaleItems());
            this._resizeObserver.observe(this.$refs.canvas);

            // tavola senza item salvati da posizionare: lo stato iniziale è già completo
            if (!this._savedItems.length) {
                this.markSaved();
            }

            // uscita dalla pagina con modifiche non salvate: i link (gestiti da barba, in ascolto
            // sul document) si intercettano in capture prima di lui; ricarica e chiusura della
            // scheda col dialogo nativo del browser
            this._onLinkClick = (e) => this.interceptLeave(e);
            this._onBeforeUnload = (e) => {
                if (!this.isSaving && !this.isLeaving && this.hasUnsavedChanges()) e.preventDefault();
            };
            document.addEventListener('click', this._onLinkClick, true);
            window.addEventListener('beforeunload', this._onBeforeUnload);

            // guest: ogni modifica alla tavola programma il salvataggio automatico
            if (!this.isLogged) {
                this.$watch(() => this.snapshot(), () => this.scheduleAutosave());
            }
        },

        // chiamato automaticamente da Alpine quando barba rimuove il container
        destroy() {
            document.removeEventListener('pointermove', this._onMouseMove);
            document.removeEventListener('pointerup', this._onMouseUp);
            document.removeEventListener('pointercancel', this._onMouseUp);
            this._resizeObserver.disconnect();
            document.removeEventListener('click', this._onLinkClick, true);
            window.removeEventListener('beforeunload', this._onBeforeUnload);
            clearTimeout(this._autosaveTimer);
        },

        // Riscala posizione e dimensioni degli elementi in proporzione alla nuova larghezza del canvas
        scaleItems() {
            const width = this.$refs.canvas.clientWidth;
            // larghezza 0 = tavola nascosta (es. mobile in verticale): teniamo l'ultima misura valida
            if (!width || width === this.canvasWidth) return;

            if (this.canvasWidth) {
                const ratio = width / this.canvasWidth;
                this.items.forEach(item => {
                    item.x *= ratio;
                    item.y *= ratio;
                    item.width *= ratio;
                    item.height *= ratio;
                });
            }
            this.canvasWidth = width;
            this.placeSavedItems();
        },

        // Porta sul canvas gli item salvati, convertendo le misure da % a px.
        // Con il canvas nascosto (larghezza 0) si rimanda al primo scaleItems utile
        placeSavedItems() {
            const canvas = this.$refs.canvas;
            if (!this._savedItems.length || !canvas.clientWidth) return;

            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            this.items = this._savedItems.map(item => ({
                ...item,
                id: String(item.id),
                serverId: item.id,  // riga salvata: al prossimo salvataggio si aggiorna
                sourceId: null,
                x: item.left / 100 * width,
                y: item.top / 100 * height,
                width: item.width / 100 * width,
                height: item.height / 100 * height,
                currentImageIndex: item.imageIndex < item.images.length ? item.imageIndex : 0
            }));
            this._savedItems = [];
            this.markSaved();
        },

        // ============================================
        // DRAG & DROP DALLA PALETTE AL CANVAS
        // ============================================

        // Necessario per permettere il drop (comportamento di default del browser lo impedirebbe)
        allowDrop(e) {
            e.preventDefault();
            e.stopPropagation();
        },

        // Avviato quando si inizia a trascinare una card dalla palette (prodotto o materiale)
        startDragProduct(e, product) {
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('product', JSON.stringify(product));
        },

        // Rilascio della card sul canvas: crea un nuovo item centrato nella posizione del mouse
        dropItem(e) {
            e.preventDefault();
            e.stopPropagation();

            const productData = e.dataTransfer.getData('product');
            if (!productData) return;

            try {
                const rect = this.$refs.canvas.getBoundingClientRect();
                this.addFromPalette(JSON.parse(productData), e.clientX - rect.left, e.clientY - rect.top);
            } catch (error) {
                console.error('Errore nel drag&drop:', error);
            }
        },

        // Aggiunge al canvas un prodotto/materiale della palette (bottone "+" o drop).
        // Senza coordinate lo posiziona al centro, leggermente sfalsato ad ogni aggiunta
        // per non sovrapporre esattamente gli elementi.
        addFromPalette(product, centerX = null, centerY = null) {
            const rect = this.$refs.canvas.getBoundingClientRect();
            const isMaterial = product.type === 'material';
            // dimensioni iniziali in proporzione alla tavola (circa 1/5 della larghezza per i prodotti)
            const width = Math.round(rect.width * (isMaterial ? 0.15 : 0.21));
            const height = isMaterial ? Math.round(width * 1.25) : width;

            if (centerX === null) {
                const shift = (this.items.length % 6) * 24;
                centerX = rect.width / 2 - 60 + shift;
                centerY = rect.height / 2 - 60 + shift;
            }

            this.addItem({
                type: product.type,
                name: product.name,
                subtitle: product.subtitle || '',
                category: product.category || '',
                code: product.code || '',
                x: Math.max(0, Math.min(centerX - width / 2, rect.width - width)),
                y: Math.max(0, Math.min(centerY - height / 2, rect.height - height - 40)),
                width,
                height,
                zIndex: Math.max(...this.items.map(i => i.zIndex), 0) + 1, // sopra a tutti gli altri
                sourceId: product.id, // collega l'item al prodotto/materiale originale (riga di palette)
                serverId: null,       // non ancora salvato
                notes: '',
                images: product.images,     // galleria di immagini del prodotto/materiale ({ src, srcset, ratio })
                currentImageIndex: 0        // parte sempre dalla prima immagine
            });

            this.paletteOpen = false;
        },

        // Toglie un prodotto/materiale dalla palette (bottone "rimuovi"), anche lato backend.
        // Gli item non ancora salvati trascinati da lì si creano dalla riga di palette (sourceId):
        // prima si salva la tavola, senza salvataggio (es. titolo mancante) la rimozione non avviene
        async removeFromPalette(product) {
            const hasUnsavedCopies = this.items.some(item => !item.serverId && item.sourceId === product.id);
            if (hasUnsavedCopies && !(await this.save())) return;

            await this._wire.removePaletteItem(product.id);

            const list = product.type === 'material' ? 'materials' : 'products';
            this[list] = this[list].filter(p => p.id !== product.id);
        },

        // Crea un nuovo elemento sul canvas e lo seleziona subito
        addItem(itemData) {
            const newItem = {
                id: Date.now().toString(), // id univoco semplice, sufficiente per uso client-side
                ...itemData
            };
            this.items.push(newItem);
            this.selectedItemId = newItem.id;
        },

        // ============================================
        // SELEZIONE
        // ============================================

        selectItem(id) {
            this.selectedItemId = id;
        },

        // ============================================
        // SPOSTAMENTO (DRAG) DI UN ELEMENTO GIÀ SUL CANVAS
        // ============================================

        // Al mousedown sull'elemento: memorizza l'offset del mouse rispetto all'angolo dell'item.
        // Non tocca ancora lo z-index: lo facciamo solo se il mouse si muove davvero (vedi handleMouseMove),
        // così un semplice click di selezione non altera l'ordine dei livelli.
        startDrag(e, itemId) {
            if (e.target.classList.contains('c-moodboard-resize')) return; // il resize ha la sua gestione separata

            const item = this.items.find(i => i.id === itemId);
            if (!item) return;

            this.draggedItem = itemId;
            this.dragStarted = false;

            const rect = this.$refs.canvas.getBoundingClientRect();
            this.dragOffset.x = (e.clientX - rect.left) - item.x;
            this.dragOffset.y = (e.clientY - rect.top) - item.y;

            e.preventDefault(); // evita selezione testo / scroll durante il trascinamento

            // Misuriamo l'altezza REALE del blocco (immagine + titolo + note, che può essere più alto
            // di item.height a causa del testo della nota), per non far tagliare titolo/note dal canvas
            const el = e.currentTarget;
            this.dragElementSize.width = el.offsetWidth;
            this.dragElementSize.height = el.offsetHeight;
        },

        // ============================================
        // RIDIMENSIONAMENTO (RESIZE) DI UN ELEMENTO
        // ============================================

        // Al mousedown su una maniglia: memorizza dimensioni/posizione di partenza,
        // necessarie per calcolare correttamente il delta durante il movimento
        startResize(e, itemId, direction) {
            const item = this.items.find(i => i.id === itemId);
            if (!item) return;

            e.preventDefault();

            this.resizingItem = itemId;
            this.resizeDirection = direction;
            this.resizeStart = {
                x: e.clientX,
                y: e.clientY,
                width: item.width,
                height: item.height,
                itemX: item.x,
                itemY: item.y
            };
        },

        // ============================================
        // GESTIONE MOVIMENTO MOUSE (drag + resize)
        // ============================================

        handleMouseMove(e) {
            // --- DRAG: spostamento di un elemento esistente ---
            if (this.draggedItem) {
                const item = this.items.find(i => i.id === this.draggedItem);
                if (!item) return;

                const rect = this.$refs.canvas.getBoundingClientRect();

                // Il drag si considera "iniziato" solo dopo un movimento minimo del mouse (5px).
                // In questo modo un semplice click (senza movimento) seleziona l'elemento
                // senza portarlo automaticamente in primo piano.
                if (!this.dragStarted) {
                    const deltaX = Math.abs(e.clientX - (rect.left + item.x + this.dragOffset.x));
                    const deltaY = Math.abs(e.clientY - (rect.top + item.y + this.dragOffset.y));

                    if (deltaX > 5 || deltaY > 5) {
                        this.dragStarted = true;
                        // Ora che il drag è reale, portiamo l'elemento in primo piano
                        const maxZ = Math.max(...this.items.map(i => i.zIndex), 0);
                        item.zIndex = maxZ + 1;
                    }
                }

                // Aggiorna la posizione, restando dentro ai confini del canvas.
                // Usiamo le dimensioni REALI misurate in startDrag (non item.width/height,
                // che rappresentano solo la dimensione dell'immagine e non includono titolo/note)
                item.x = Math.max(0, Math.min(e.clientX - rect.left - this.dragOffset.x, rect.width - this.dragElementSize.width));
                item.y = Math.max(0, Math.min(e.clientY - rect.top - this.dragOffset.y, rect.height - this.dragElementSize.height));
            }

            // --- RESIZE: ridimensionamento da una delle 8 maniglie ---
            if (this.resizingItem) {
                const item = this.items.find(i => i.id === this.resizingItem);
                if (!item) return;

                const deltaX = e.clientX - this.resizeStart.x;
                const deltaY = e.clientY - this.resizeStart.y;
                const dir = this.resizeDirection;
                const minSize = 50; // dimensione minima per evitare elementi troppo piccoli

                // Lato destro: la larghezza cresce/decresce, la posizione X non cambia
                if (dir === 'r' || dir === 'tr' || dir === 'br') {
                    item.width = Math.max(minSize, this.resizeStart.width + deltaX);
                }

                // Lato sinistro: la larghezza cambia E la posizione X deve compensare,
                // altrimenti l'elemento si sposterebbe invece di ridimensionarsi
                if (dir === 'l' || dir === 'tl' || dir === 'bl') {
                    const newWidth = Math.max(minSize, this.resizeStart.width - deltaX);
                    const widthDiff = newWidth - this.resizeStart.width;
                    item.x = this.resizeStart.itemX - widthDiff;
                    item.width = newWidth;
                }

                // Lato basso: stessa logica del lato destro, ma sull'altezza
                if (dir === 'b' || dir === 'bl' || dir === 'br') {
                    item.height = Math.max(minSize, this.resizeStart.height + deltaY);
                }

                // Lato alto: stessa logica del lato sinistro, ma sull'altezza/Y
                if (dir === 't' || dir === 'tl' || dir === 'tr') {
                    const newHeight = Math.max(minSize, this.resizeStart.height - deltaY);
                    const heightDiff = newHeight - this.resizeStart.height;
                    item.y = this.resizeStart.itemY - heightDiff;
                    item.height = newHeight;
                }
            }
        },

        // Fine di qualsiasi drag/resize in corso
        handleMouseUp() {
            this.draggedItem = null;
            this.dragStarted = false;
            this.resizingItem = null;
            this.resizeDirection = null;
        },

        // La toolbar dell'elemento sta alla sua destra: se lì non c'è spazio
        // (il canvas taglia ciò che esce dai bordi) la spostiamo a sinistra
        toolbarOnLeft(item) {
            return item.x + item.width + 48 > this.$refs.canvas.clientWidth;
        },

        // ============================================
        // GESTIONE Z-INDEX (ordine dei livelli)
        // ============================================

        // Sposta l'elemento selezionato di un livello avanti ('up') o indietro ('down').
        // Importante: NON riordiniamo l'array items, scambiamo solo i valori di zIndex.
        // Questo evita il bug per cui, spostando ripetutamente lo stesso elemento,
        // l'ordine "salta" in modo imprevedibile.
        moveLayer(direction) {
            const item = this.items.find(i => i.id === this.selectedItemId);
            if (!item) return;

            // Ordiniamo una copia degli elementi in base al loro z-index attuale,
            // per sapere chi sta "prima" e "dopo" l'elemento selezionato
            const sorted = [...this.items].sort((a, b) => a.zIndex - b.zIndex);
            const currentPos = sorted.findIndex(i => i.id === this.selectedItemId);

            if (direction === 'up' && currentPos < sorted.length - 1) {
                // Scambia lo z-index con l'elemento immediatamente sopra
                const temp = sorted[currentPos].zIndex;
                sorted[currentPos].zIndex = sorted[currentPos + 1].zIndex;
                sorted[currentPos + 1].zIndex = temp;
            } else if (direction === 'down' && currentPos > 0) {
                // Scambia lo z-index con l'elemento immediatamente sotto
                const temp = sorted[currentPos].zIndex;
                sorted[currentPos].zIndex = sorted[currentPos - 1].zIndex;
                sorted[currentPos - 1].zIndex = temp;
            }
        },

        // ============================================
        // ELIMINAZIONE ELEMENTI
        // ============================================

        // Attributo sizes dell'immagine di un item: larghezza a cui è mostrata con object-fit: cover
        // (quella del box o, se il box è più alto in proporzione, quella che ne copre l'altezza).
        // Legato a item.width/height: al resize dell'item o della tavola il browser sceglie dallo srcset
        imageSizes(item) {
            const image = item.images[item.currentImageIndex];
            return Math.ceil(Math.max(item.width, item.height * (image?.ratio || 1))) + 'px';
        },

        // Rimuove un singolo elemento dal canvas
        deleteItem(id) {
            this.items = this.items.filter(i => i.id !== id);
            if (this.selectedItemId === id) {
                this.selectedItemId = null;
            }
        },

        // Svuota completamente il canvas
        clearAll() {
            this.items = [];
            this.selectedItemId = null;
        },

        // ============================================
        // SALVATAGGIO / INVIO (solo utenti loggati)
        // ============================================

        // Stato completo della moodboard, da passare al backend: posizioni e dimensioni
        // in % del canvas, così la composizione non dipende dalla larghezza dello schermo
        payload() {
            const canvas = this.$refs.canvas;
            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            return {
                title: this.title,
                notes: this.boardNotes,
                canvas: { width, height },
                items: this.items.map(item => ({
                    id: item.serverId,
                    sourceId: item.sourceId,
                    left: item.x / width * 100,
                    top: item.y / height * 100,
                    width: item.width / width * 100,
                    height: item.height / height * 100,
                    zIndex: item.zIndex,
                    imageIndex: item.currentImageIndex,
                    notes: item.notes || null
                }))
            };
        },

        // Salva titolo, note e item della tavola; gli item ricevono l'id della riga salvata,
        // così il salvataggio successivo li aggiorna invece di duplicarli. Restituisce true se ha salvato
        async save() {
            if (this.isSaving) return false;

            if (!this.title.trim()) {
                this.titleMissing = true;
                this.$refs.title.focus();
                return false;
            }

            const savedItems = [...this.items];
            const { title, notes, canvas, items } = this.payload();

            this.isSaving = true;
            try {
                const ids = await this._wire.save(title, notes, canvas, items);
                (ids || []).forEach((id, index) => { savedItems[index].serverId = id; });
                this.markSaved();
                return true;
            } finally {
                this.isSaving = false;
            }
        },

        // ============================================
        // MODIFICHE NON SALVATE / USCITA DALLA PAGINA
        // ============================================

        // Stato della tavola da confrontare con quello salvato: misure relative alla larghezza
        // del canvas (non cambiano al resize), arrotondate. La nota degli item già salvati
        // è esclusa perché si salva subito (v. saveNotes)
        snapshot() {
            const width = this.canvasWidth || 1;
            const relative = value => Math.round(value / width * 1000);
            return JSON.stringify({
                title: this.title,
                notes: this.boardNotes,
                items: this.items.map(item => [
                    item.serverId,
                    item.sourceId,
                    relative(item.x),
                    relative(item.y),
                    relative(item.width),
                    relative(item.height),
                    item.zIndex,
                    item.currentImageIndex,
                    item.serverId ? null : (item.notes || '')
                ])
            });
        },

        markSaved() {
            this._savedSnapshot = this.snapshot();
        },

        // Solo con gli item salvati già sulla tavola
        hasUnsavedChanges() {
            return this._savedSnapshot !== null && !this._savedItems.length && this.snapshot() !== this._savedSnapshot;
        },

        // Click su un link verso un'altra pagina con modifiche non salvate: blocca il cambio pagina
        // e apre il modal. Esclusi nuova scheda, download, ancore della stessa pagina, mailto/tel
        interceptLeave(e) {
            if (this.isLeaving || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

            // esclude link in blank e download
            const link = e.target.closest('a[href]');
            if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
            
            const url = new URL(link.href, window.location.href);
            const current = window.location;
            // esclude link alla stessa pagina e àncore
            if (!url.protocol.startsWith('http')) return;
            if (url.origin === current.origin && url.pathname === current.pathname && url.search === current.search) return;
            if (!this.hasUnsavedChanges()) return;

            e.preventDefault();
            e.stopImmediatePropagation();

            this._leaveUrl = url.href;
            // verifica se devo uscire con barba (vedi modal)
            this._leaveWithBarba = !!window.barba && url.origin === current.origin && !link.closest('[data-barba-prevent]');

            // guest: niente modale, salva ed esce
            if (!this.isLogged) {
                this.autosaveAndLeave();
                return;
            }

            this.showLeaveModal = true;
        },

        // Esce verso la pagina scelta; fullLoad forza il caricamento classico
        leave(fullLoad = false) {
            this.isLeaving = true;
            this.showLeaveModal = false;

            if (this._leaveWithBarba && !fullLoad) {
                window.barba.go(this._leaveUrl);
            } else {
                window.location.href = this._leaveUrl;
            }
        },

        // "Salva" nel modale: salva ed esce. Una tavola nuova al salvataggio viene reindirizzata
        // al suo progetto (v. MoodboardBoard::save()): il caricamento classico verso la pagina
        // scelta prende il posto di quel redirect. Senza titolo resta sulla tavola (v. save)
        async saveAndLeave() {
            const isNewBoard = this._wire.projectId === null;
            this.showLeaveModal = false;

            if (await this.save()) {
                this.leave(isNewBoard);
            }
        },

        // Chiude il modale e resta sulla tavola
        cancelLeave() {
            this.showLeaveModal = false;
            this._leaveUrl = null;
        },

        // ============================================
        // SALVATAGGIO AUTOMATICO (solo guest)
        // ============================================

        // Il guest lavora sul suo progetto temporaneo (v. App\Moodboard\MoodboardOwner); senza
        // progetto il primo salvataggio lo crea, restando sulla tavola (v. MoodboardBoard::save()).
        // Il titolo è obbligatorio
        canAutosave() {
            return !this.isLogged && this.title.trim() !== '';
        },

        // Salvataggio poco dopo l'ultima modifica, per non salvare a ogni passo di un'azione
        scheduleAutosave() {
            clearTimeout(this._autosaveTimer);
            this._autosaveTimer = setTimeout(() => this.autosave(), 1000);
        },

        // Con drag/resize o un salvataggio in corso si rimanda; le modifiche arrivate durante
        // il salvataggio hanno già programmato il successivo (v. $watch in init)
        async autosave() {
            clearTimeout(this._autosaveTimer);

            if (this.draggedItem || this.resizingItem || this.isSaving) {
                this.scheduleAutosave();
                return;
            }
            if (!this.canAutosave() || !this.hasUnsavedChanges()) return;

            try {
                await this.save();
            } catch (error) {
                console.error('Errore nel salvataggio automatico:', error);
            }
        },

        // Uscita del guest: attende l'eventuale salvataggio in corso, salva le ultime modifiche ed esce
        async autosaveAndLeave() {
            this.isLeaving = true;

            while (this.isSaving) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            await this.autosave();

            this.leave();
        },

        // ============================================
        // GESTIONE NOTE
        // ============================================

        // Apre il modale precompilato con le note già esistenti (se presenti)
        openNotesEditor(itemId) {
            const item = this.items.find(i => i.id === itemId);
            if (item) {
                this.editingNotes = itemId;
                this.notesText = item.notes || '';
                this.showNotesModal = true;
                this.$nextTick(() => this.$refs.notesTextarea.focus());
            }
        },

        // Salva le note sull'item e chiude il modale
        // Aggiorna la nota dell'elemento; se l'elemento è già salvato
        // la nota si salva subito, gli altri la salvano con "Salva" della tavola
        async saveNotes() {
            const item = this.items.find(i => i.id === this.editingNotes);
            if (!item || this.isSavingNotes) return;

            if (item.serverId) {
                this.isSavingNotes = true;
                try {
                    await this._wire.saveItemNote(item.serverId, this.notesText);
                } finally {
                    this.isSavingNotes = false;
                }
            }

            item.notes = this.notesText;
            this.cancelEditingNotes();
        },

        // Chiude il modale senza salvare
        cancelEditingNotes() {
            this.showNotesModal = false;
            this.editingNotes = null;
            this.notesText = '';
        },

        // ============================================
        // GESTIONE GALLERY IMMAGINI
        // ============================================

        // Passa all'immagine successiva, tornando alla prima se si è all'ultima (loop)
        nextImage(itemId) {
            const item = this.items.find(i => i.id === itemId);
            if (!item) return;
            item.currentImageIndex = (item.currentImageIndex + 1) % item.images.length;
        },

        // Passa all'immagine precedente, tornando all'ultima se si è alla prima (loop)
        prevImage(itemId) {
            const item = this.items.find(i => i.id === itemId);
            if (!item) return;
            item.currentImageIndex = (item.currentImageIndex - 1 + item.images.length) % item.images.length;
        }
    };
}
